import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';

interface SlideData {
  id: string;
  title: string;
  type: 'dashboard' | 'assets' | 'work-orders' | 'inventory';
  content: any;
}

@customElement('pristinemaint-slideshow')
export class PristineMaintSlideshow extends LitElement {
  @state()
  private currentSlide = 0;

  @state()
  private isPlaying = true;

  private slideInterval?: number;

  static styles = css`
    :host {
      display: block;
      width: 100%;
      height: 100%;
    }

    .slideshow-container {
      position: relative;
      width: 100%;
      height: 100%;
      overflow: hidden;
      border-radius: 12px;
    }

    .slide {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      opacity: 0;
      transform: translateX(100%);
      transition: all 0.5s ease-in-out;
      background: white;
      border-radius: 12px;
      box-shadow: var(--sl-shadow-large);
      overflow: hidden;
    }

    .slide.active {
      opacity: 1;
      transform: translateX(0);
    }

    .slide.prev {
      transform: translateX(-100%);
    }

    .slide-content {
      padding: 1.5rem;
      height: 100%;
      overflow-y: auto;
    }

    .slide-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 1rem;
      padding-bottom: 0.75rem;
      border-bottom: 1px solid var(--sl-color-neutral-200);
    }

    .slide-title {
      font-size: 1.125rem;
      font-weight: 600;
      color: var(--sl-color-neutral-900);
      margin: 0;
    }

    .slide-type {
      font-size: 0.75rem;
      color: var(--sl-color-neutral-500);
      background: var(--sl-color-neutral-100);
      padding: 0.25rem 0.5rem;
      border-radius: 4px;
    }

    .controls {
      position: absolute;
      bottom: 1rem;
      left: 50%;
      transform: translateX(-50%);
      display: flex;
      gap: 0.5rem;
      z-index: 10;
    }

    .control-btn {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      border: none;
      background: var(--sl-color-neutral-300);
      cursor: pointer;
      transition: all 0.2s ease;
    }

    .control-btn.active {
      background: var(--sl-color-primary-500);
      transform: scale(1.2);
    }

    .play-pause-btn {
      position: absolute;
      top: 1rem;
      right: 1rem;
      width: 32px;
      height: 32px;
      border: none;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.9);
      backdrop-filter: blur(4px);
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--sl-color-neutral-700);
      transition: all 0.2s ease;
      z-index: 10;
    }

    .play-pause-btn:hover {
      background: white;
      transform: scale(1.1);
    }

    /* Dashboard Slide Styles */
    .dashboard-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 1rem;
      height: calc(100% - 3rem);
    }

    .metric-card {
      background: var(--sl-color-neutral-50);
      border-radius: 8px;
      padding: 1rem;
      text-align: center;
    }

    .metric-value {
      font-size: 2rem;
      font-weight: 700;
      color: var(--sl-color-primary-600);
      margin: 0.5rem 0;
    }

    .metric-label {
      font-size: 0.875rem;
      color: var(--sl-color-neutral-600);
      margin: 0;
    }

    .chart-container {
      grid-column: 1 / -1;
      background: var(--sl-color-neutral-50);
      border-radius: 8px;
      padding: 1rem;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .donut-chart {
      width: 120px;
      height: 120px;
      border-radius: 50%;
      background: conic-gradient(
        var(--sl-color-primary-500) 0deg 120deg,
        var(--sl-color-warning-500) 120deg 240deg,
        var(--sl-color-success-500) 240deg 360deg
      );
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-weight: bold;
      font-size: 1.25rem;
    }

    /* Data Table Styles */
    .data-table {
      width: 100%;
      border-collapse: collapse;
      font-size: 0.875rem;
    }

    .data-table th,
    .data-table td {
      padding: 0.5rem;
      text-align: left;
      border-bottom: 1px solid var(--sl-color-neutral-200);
    }

    .data-table th {
      background: var(--sl-color-neutral-100);
      font-weight: 600;
      color: var(--sl-color-neutral-700);
    }

    .data-table td {
      color: var(--sl-color-neutral-600);
    }

    .badge {
      padding: 0.25rem 0.5rem;
      border-radius: 12px;
      font-size: 0.75rem;
      font-weight: 500;
    }

    .badge-success {
      background: var(--sl-color-success-100);
      color: var(--sl-color-success-700);
    }

    .badge-warning {
      background: var(--sl-color-warning-100);
      color: var(--sl-color-warning-700);
    }

    .badge-danger {
      background: var(--sl-color-danger-100);
      color: var(--sl-color-danger-700);
    }

    .badge-primary {
      background: var(--sl-color-primary-100);
      color: var(--sl-color-primary-700);
    }

    .badge-neutral {
      background: var(--sl-color-neutral-100);
      color: var(--sl-color-neutral-700);
    }

    /* Responsive */
    @media (max-width: 768px) {
      .dashboard-grid {
        grid-template-columns: 1fr;
      }

      .slide-content {
        padding: 1rem;
      }

      .metric-value {
        font-size: 1.5rem;
      }
    }
  `;

  private slides: SlideData[] = [
    {
      id: 'dashboard-overview',
      title: 'Dashboard Overview',
      type: 'dashboard',
      content: {
        metrics: [
          { label: 'Open Work Orders', value: 12, color: 'primary' },
          { label: 'Assets Down', value: 3, color: 'danger' },
          { label: 'PM Tasks Due', value: 8, color: 'warning' },
          { label: 'Low Stock Items', value: 5, color: 'warning' }
        ],
        chartData: {
          total: 45,
          open: 12,
          inProgress: 18,
          completed: 15
        }
      }
    },
    {
      id: 'work-orders',
      title: 'Work Orders Management',
      type: 'work-orders',
      content: {
        workOrders: [
          { id: 'WO-1204', status: 'In Progress', priority: 'High', asset: 'Conveyor Belt A', assigned: 'John Smith', created: '2024-01-15' },
          { id: 'WO-1205', status: 'Open', priority: 'Critical', asset: 'Motor Unit B', assigned: 'Sarah Johnson', created: '2024-01-14' },
          { id: 'WO-1206', status: 'Completed', priority: 'Medium', asset: 'Pump System C', assigned: 'Mike Wilson', created: '2024-01-13' },
          { id: 'WO-1207', status: 'Open', priority: 'Low', asset: 'Control Panel D', assigned: 'Lisa Brown', created: '2024-01-12' }
        ]
      }
    },
    {
      id: 'assets',
      title: 'Asset Management',
      type: 'assets',
      content: {
        assets: [
          { name: 'Conveyor Belt A', status: 'Active', location: 'Production Line 1', parent: 'Manufacturing Unit', created: '2023-06-15' },
          { name: 'Motor Unit B', status: 'Down', location: 'Production Line 2', parent: 'Manufacturing Unit', created: '2023-08-20' },
          { name: 'Pump System C', status: 'Active', location: 'Utility Room', parent: 'Infrastructure', created: '2023-04-10' },
          { name: 'Control Panel D', status: 'Active', location: 'Control Room', parent: 'Infrastructure', created: '2023-07-05' }
        ]
      }
    },
    {
      id: 'inventory',
      title: 'Inventory Management',
      type: 'inventory',
      content: {
        parts: [
          { name: 'Belt Drive Motor', stock: 15, max: 50, status: 'In Stock', cost: 125.50, lastUpdated: '2024-01-10' },
          { name: 'Hydraulic Pump', stock: 3, max: 20, status: 'Low Stock', cost: 450.00, lastUpdated: '2024-01-12' },
          { name: 'Control Circuit Board', stock: 0, max: 10, status: 'Out of Stock', cost: 89.99, lastUpdated: '2024-01-08' },
          { name: 'Safety Valve', stock: 25, max: 30, status: 'In Stock', cost: 75.25, lastUpdated: '2024-01-14' }
        ]
      }
    }
  ];

  connectedCallback() {
    super.connectedCallback();
    this.startSlideshow();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.stopSlideshow();
  }

  private startSlideshow() {
    if (this.isPlaying) {
      this.slideInterval = window.setInterval(() => {
        this.nextSlide();
      }, 4000);
    }
  }

  private stopSlideshow() {
    if (this.slideInterval) {
      clearInterval(this.slideInterval);
      this.slideInterval = undefined;
    }
  }

  private nextSlide() {
    this.currentSlide = (this.currentSlide + 1) % this.slides.length;
  }

  private goToSlide(index: number) {
    this.currentSlide = index;
  }

  private togglePlayPause() {
    this.isPlaying = !this.isPlaying;
    if (this.isPlaying) {
      this.startSlideshow();
    } else {
      this.stopSlideshow();
    }
  }

  private renderDashboardSlide(slide: SlideData) {
    const { metrics, chartData } = slide.content;
    
    return html`
      <div class="dashboard-grid">
        ${metrics.map(metric => html`
          <div class="metric-card">
            <div class="metric-value" style="color: var(--sl-color-${metric.color}-600);">
              ${metric.value}
            </div>
            <div class="metric-label">${metric.label}</div>
          </div>
        `)}
        
        <div class="chart-container">
          <div class="donut-chart">${chartData.total}</div>
          <div style="margin-left: 1rem;">
            <div style="display: flex; align-items: center; margin-bottom: 0.5rem;">
              <div style="width: 12px; height: 12px; background: var(--sl-color-primary-500); border-radius: 50%; margin-right: 0.5rem;"></div>
              <span>Open (${chartData.open})</span>
            </div>
            <div style="display: flex; align-items: center; margin-bottom: 0.5rem;">
              <div style="width: 12px; height: 12px; background: var(--sl-color-warning-500); border-radius: 50%; margin-right: 0.5rem;"></div>
              <span>In Progress (${chartData.inProgress})</span>
            </div>
            <div style="display: flex; align-items: center;">
              <div style="width: 12px; height: 12px; background: var(--sl-color-success-500); border-radius: 50%; margin-right: 0.5rem;"></div>
              <span>Completed (${chartData.completed})</span>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private renderWorkOrdersSlide(slide: SlideData) {
    const { workOrders } = slide.content;
    
    return html`
      <table class="data-table">
        <thead>
          <tr>
            <th>Work Order</th>
            <th>Status</th>
            <th>Priority</th>
            <th>Asset</th>
            <th>Assigned To</th>
            <th>Created</th>
          </tr>
        </thead>
        <tbody>
          ${workOrders.map(wo => html`
            <tr>
              <td><strong>${wo.id}</strong></td>
              <td>
                <span class="badge badge-${wo.status === 'Completed' ? 'success' : wo.status === 'In Progress' ? 'warning' : 'primary'}">
                  ${wo.status}
                </span>
              </td>
              <td>
                <span class="badge badge-${wo.priority === 'Critical' ? 'danger' : wo.priority === 'High' ? 'warning' : 'neutral'}">
                  ${wo.priority}
                </span>
              </td>
              <td>${wo.asset}</td>
              <td>${wo.assigned}</td>
              <td>${wo.created}</td>
            </tr>
          `)}
        </tbody>
      </table>
    `;
  }

  private renderAssetsSlide(slide: SlideData) {
    const { assets } = slide.content;
    
    return html`
      <table class="data-table">
        <thead>
          <tr>
            <th>Asset Name</th>
            <th>Status</th>
            <th>Location</th>
            <th>Parent Asset</th>
            <th>Created</th>
          </tr>
        </thead>
        <tbody>
          ${assets.map(asset => html`
            <tr>
              <td><strong>${asset.name}</strong></td>
              <td>
                <span class="badge badge-${asset.status === 'Active' ? 'success' : 'danger'}">
                  ${asset.status}
                </span>
              </td>
              <td>${asset.location}</td>
              <td>${asset.parent}</td>
              <td>${asset.created}</td>
            </tr>
          `)}
        </tbody>
      </table>
    `;
  }

  private renderInventorySlide(slide: SlideData) {
    const { parts } = slide.content;
    
    return html`
      <table class="data-table">
        <thead>
          <tr>
            <th>Part Name</th>
            <th>Stock Level</th>
            <th>Status</th>
            <th>Cost</th>
            <th>Last Updated</th>
          </tr>
        </thead>
        <tbody>
          ${parts.map(part => html`
            <tr>
              <td><strong>${part.name}</strong></td>
              <td>${part.stock} / ${part.max}</td>
              <td>
                <span class="badge badge-${part.status === 'In Stock' ? 'success' : part.status === 'Low Stock' ? 'warning' : 'danger'}">
                  ${part.status}
                </span>
              </td>
              <td>$${part.cost}</td>
              <td>${part.lastUpdated}</td>
            </tr>
          `)}
        </tbody>
      </table>
    `;
  }

  private renderSlideContent(slide: SlideData) {
    switch (slide.type) {
      case 'dashboard':
        return this.renderDashboardSlide(slide);
      case 'work-orders':
        return this.renderWorkOrdersSlide(slide);
      case 'assets':
        return this.renderAssetsSlide(slide);
      case 'inventory':
        return this.renderInventorySlide(slide);
      default:
        return html`<p>No content available</p>`;
    }
  }

  render() {
    return html`
      <div class="slideshow-container">
        <button class="play-pause-btn" @click=${this.togglePlayPause}>
          <sl-icon name=${this.isPlaying ? 'pause' : 'play'}></sl-icon>
        </button>

        ${this.slides.map((slide, index) => html`
          <div class="slide ${index === this.currentSlide ? 'active' : index === (this.currentSlide - 1 + this.slides.length) % this.slides.length ? 'prev' : ''}">
            <div class="slide-content">
              <div class="slide-header">
                <h3 class="slide-title">${slide.title}</h3>
                <span class="slide-type">${slide.type.replace('-', ' ').toUpperCase()}</span>
              </div>
              ${this.renderSlideContent(slide)}
            </div>
          </div>
        `)}

        <div class="controls">
          ${this.slides.map((_, index) => html`
            <button 
              class="control-btn ${index === this.currentSlide ? 'active' : ''}"
              @click=${() => this.goToSlide(index)}
            ></button>
          `)}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cmms-slideshow': CMMSSlideshow;
  }
}
